import React, { useContext } from "react";
import { t } from "ttag";

import { constants } from "@reactivated/constants";

import { assertNever } from "@thelabnyc/thelabui/src/utils/never";

import { ReactivatedContext as Context } from "../../context";
import { MILES_TO_METERS, Meters, isoMeters } from "../../models";
import { getPreferredDistanceUnit } from "./Distance";

import styles from "./DistanceSelect.module.scss";

const DistanceUnit = constants["server.cms.types.DistanceUnit"];
type DistanceUnit = (typeof DistanceUnit)[keyof typeof DistanceUnit];

const KM_TO_METERS = 1000;

type DistanceOption = {
    label: string;
    meters: number;
};

const buildOptions = (unit: DistanceUnit): DistanceOption[] => {
    switch (unit) {
        case DistanceUnit.MI:
            return [10, 25, 50, 100].map((mi) => ({
                label: t`${mi} miles`,
                meters: mi * MILES_TO_METERS,
            }));
        case DistanceUnit.KM:
            return [15, 40, 80, 160].map((distKMs) => ({
                label: t`${distKMs} km`,
                meters: distKMs * KM_TO_METERS,
            }));
        default:
            return assertNever(unit);
    }
};

export const DistanceSelect = (props: {
    radius: Meters;
    onRadiusChange: (radius: Meters) => void;
}) => {
    const context = useContext(Context);
    const unit = getPreferredDistanceUnit(context.DISTANCE_UNIT);
    const options = buildOptions(unit);

    return (
        <label className={styles.label}>
            {t`Select distance:`}
            <select
                className={styles.select}
                value={isoMeters.unwrap(props.radius)}
                onChange={(e) =>
                    props.onRadiusChange(isoMeters.wrap(Number(e.target.value)))
                }
            >
                {options.map((option) => (
                    <option key={option.meters} value={option.meters}>
                        {option.label}
                    </option>
                ))}
            </select>
        </label>
    );
};
